import { datadogRum } from '@datadog/browser-rum';
import { OrganizationSchema } from '../definitions/schemas';
import { UserAuth } from '../definitions/userAuth';

/**
 * @see https://gitlab.com/mindtickle/mt-shell/-/blob/track/integration/packages/ui-shell/src/components/Datadog/config/constants.ts
 */
const COMMON_TAGS = {
  APP_NAME: 'app',
  NETWORK: 'network',
  STREAM_NAME: 'stream',
};

/**
 * @see https://gitlab.com/mindtickle/mt-shell/-/blob/track/integration/packages/ui-shell/src/components/Datadog/helpers/index.ts#L15
 */
const COMPANY_TAGS = {
  ID: 'id',
  IS_CUSTOMER: 'isCustomer',
  LEARNING_SITE: 'learningSite',
  NAME: 'name',
};

/**
 * @see https://gitlab.com/mindtickle/mt-shell/-/blob/track/integration/packages/ui-shell/src/components/Datadog/config/constants.ts
 */
const CONTEXT_NAMES = {
  COMPANY: 'company',
  OWNER: 'owner',
  SHELL: 'shell',
  APP_LEVEL_DATA: 'appLevelData',
};

type ContextValue = Record<string, any>;

const addGlobalContext = (name: string, value: ContextValue) => {
  datadogRum.addRumGlobalContext(name, value);
};

const updateGlobalContext = (name: string, value: ContextValue) => {
  const ctx = datadogRum.getRumGlobalContext();
  datadogRum.setRumGlobalContext({ ...ctx, [name]: value });
};

const setGlobalContext = (name: string, value: ContextValue) => {
  const ctx = datadogRum.getRumGlobalContext();
  ctx[name] ? updateGlobalContext(name, value) : addGlobalContext(name, value);
};

const buildCompanyContext = (
  organization: OrganizationSchema,
  userAuth?: UserAuth
) => {
  const { id, vendor, vendorTenantId, vendorTenantUrl } = organization;

  let orgId = vendor?.analyticsPrefix ? `${vendor.analyticsPrefix}-${id}` : id;
  let learningSite = window.location.hostname;

  if (vendorTenantId && vendorTenantUrl) {
    const url = new URL(vendorTenantUrl);
    orgId = vendorTenantId;
    learningSite = url.hostname;
  }

  return {
    [COMPANY_TAGS.ID]: orgId,
    [COMPANY_TAGS.IS_CUSTOMER]: userAuth?.company?.type === 'CUSTOMER',
    [COMPANY_TAGS.LEARNING_SITE]: learningSite,
    [COMPANY_TAGS.NAME]: organization.name,
  };
};

const buildOwnerContext = () => ({
  [COMMON_TAGS.APP_NAME]: 'dsr',
  [COMMON_TAGS.STREAM_NAME]: 'dsr',
});

// ~~~~~~~~~~ Exposed helper functions ~~~~~~~~~~ //

const initializeContext = (org?: OrganizationSchema, userAuth?: UserAuth) => {
  setGlobalContext(CONTEXT_NAMES.OWNER, buildOwnerContext());
  setGlobalContext(CONTEXT_NAMES.SHELL, {
    [COMMON_TAGS.NETWORK]:
      (navigator as any)?.connection?.effectiveType || 'unknown',
  });
  if (org) {
    setGlobalContext(CONTEXT_NAMES.COMPANY, buildCompanyContext(org, userAuth));
  }
};

const sendCustomTiming = (name: string, time?: number | undefined) => {
  datadogRum.addTiming(name, time);
};

const identify = (
  userId: string,
  org?: OrganizationSchema,
  userAuth?: UserAuth
) => {
  console.info(`Datadog identify user: ${userId}`);
  // for PII compliance, only user id can be tracked
  datadogRum.setUser({ id: userId });
  initializeContext(org, userAuth);
};

export default {
  identify,
  sendCustomTiming,
  setGlobalContext,
};
